import { env } from '@hb/NBridge/lib/h5';
import { getPageModule } from '@/api/common';
import {
  getVirtualCoupons,
  couponOrderCreate,
  detailCouponOrderCreate,
  getTaskAwardDetail,
  finishTask,
  queryDeliverBanner,
  getEducationPopupWindow,
  eventUpload,
  getAutoDeductStatus,
  updateAutoDeductStatus,
  getWelfareStyleVersion,
  getUserComponent,
  checkIsWelfareHitchCarFare,
} from '@/api/fuli';
import { getTokenAndKey, getTokenAndLocation } from '@/utils';
import {
  safeJsonStringify,
  isWithinDays,
  compareVersions,
  generateUUID,
} from '@/utils/tools';
import { getVersion } from '@/utils/systemCode';
import { Storage } from '@/utils/storage';
import { newMapTaskV2 } from '@/utils/mappers';
import Toast from '@/components/toast';
import { ParamsUtil } from '@/utils/params';
import eventBus from '@/const/eventBus';
import { TASK_STATUS_V2 } from '@/const/task';
import Axios from 'axios';
import SPM from './spm';
import {
  pushNotificationEnabled,
  loadPlatformAds,
  showPlatformAds,
  mobileAdReplacementWithLink,
  openAllAdsURLAndTrack,
  getManufacturer,
} from './bridge';
import {
  REWARD_GUIDE_RULES_DEFAULT,
  FULI_MODULE,
  VIDEO_RED_PACKET_DEFAULT_ICON,
  SIGN_IN_CARD_ICON_MAP,
  STORAGE_KEY_SHOW_REVISIT_GUIDE_TIME,
  TASK_MODULE_TYPE,
  WELFARE_PLAY_GUIDE_LIST,
  PLAY_GUIDE_DEFAULT_IMGS,
  MARKET_PLAN,
  WELFARE_CENTER_PAGE_CONFIG,
  WELFARE_CENTER_COMMON_HEADER_INFO,
  WELFARE_CENTER_HITCH_HEADER_INFO,
} from '../consts';

/**
 * @description 处理弹窗的广告参数
 * @function processAdParams
 * @param {*} params 广告参数
 * @returns
 */
export const processAdParams = (params) => {
  // return {
  //   rewardCount: 10,
  //   taskCode: "TK2229566568839458",
  //   adUnitId: "28df39b08dcc40b4a32bceb2e1a81d7a",
  // };

  // 处理 params 中的 adsInfo
  let adsInfo = {};
  const processedParams = { ...params };
  if (processedParams.bannerAdvertisementUnitId
    && processedParams.bannerTaskRelationCouponInfo
    && processedParams.bannerTaskRelationCouponInfo.offerList
    && processedParams.bannerTaskRelationCouponInfo.offerList.length) {
    const {
      taskCode = '',
    } = processedParams.bannerTaskRelationCouponInfo;
    const rewardCount = processedParams.bannerTaskRelationCouponInfo.offerList[0].offerAmount;
    adsInfo = {
      adUnitId: processedParams.bannerAdvertisementUnitId,
      taskCode,
      rewardCount,
      // extraAdUnitId是放在‘签到反馈弹窗’中纯展示作用。
      // 如果用户看过，也是完成‘签到弹窗’中广告对应的任务。
      // 这是为了解决两个弹窗中任看一个广告，就完成任务。
      extraAdUnitId: processedParams.extraAdUnitId || '',
    };
  }
  return adsInfo;
};

/**
 * @description 签到列表数据适配器
 * @function signInListAdapter
 * @param {*} list 原始列表
 * @returns
 */
export function signInListAdapter(list, { didSignToday = false } = {}) {
  const signInCardList = [];
  const signInPopupList = [];

  if (!list || list.length === 0) {
    return {
      signInCardList,
      signInPopupList,
    };
  }

  list.forEach((item, index) => {
    const {
      bonusCount, bgStyle, dayText, signSource, newcomerSignBonusCount,
    } = item || {};
    // bgStyle=3 已签到用 signed 图标，其余用 unsigned
    const isSigned = bgStyle === 3;

    // 根据 bgStyle 生成卡片文案
    // bgStyle: 1=空白(待签到) / 2=高亮(当前天) / 3=置灰(已签到) / 4=红包
    let text = dayText;
    if (!text) {
      if (isSigned) {
        text = '已领取';
      } else if (bgStyle === 2) {
        text = didSignToday ? '明天领' : '今天';
      } else {
        text = `第${index + 1}天`;
      }
    }

    // 卡片签到数据列表
    signInCardList.push({
      text,
      amount: bonusCount,
      bgStyle: bgStyle || 1,
      icon: isSigned
        ? SIGN_IN_CARD_ICON_MAP.signed
        : SIGN_IN_CARD_ICON_MAP.unsigned,
      signSource: signSource || '',
      newcomerSignBonusCount: Number(newcomerSignBonusCount) || 0,
    });

    // 弹窗签到数据列表
    signInPopupList.push(item);
  });

  return {
    signInCardList,
    signInPopupList,
  };
}

/**
 * @description 新人引导兑换商品列表数据适配器
 * @function exchangeGoodsListAdapter
 * @param {*} list 原始列表
 * @returns
 */
export function exchangeGoodsListAdapter(list) {
  if (list.length === 0) {
    return [];
  }

  return list.map((item) => {
    const {
      itemName,
      virtualSalePrice,
      listItemUrl,
      soldNum,
      originalPrice,
      skuId,
      saleNum,
      virtualType,
      salePriceAfterDeduction,
      pointsDeduction,
      salePrice,
      itemId,
      promotionInfos,
    } = item || {};
    return {
      title: itemName,
      amount: virtualSalePrice,
      image: listItemUrl,
      soldNum,
      cost: virtualSalePrice || 0,
      price: originalPrice || 0,
      skuId,
      saleNum,
      virtualType,
      salePriceAfterDeduction: salePriceAfterDeduction || 0,
      pointsDeduction: pointsDeduction || 0,
      salePrice,
      itemId,
      promotionInfos: promotionInfos && promotionInfos.map((v) => ({
        ...v,
        worth: v.deductionAmount,
      })),
    };
  });
}

/**
 * @description 获取新人引导兑换商品列表 前面
 * @function getExchangeGoodsList
 * @returns
 */
export async function getExchangeGoodsList(params) {
  const { type, pageSize = 4 } = params || {};

  const { token, adCode, cityCode } = await getTokenAndLocation();
  let showChannelId;
  if (env.isEasyBikeApp) {
    showChannelId = '1'; // 其实该模块只会在app中展示
  } else if (env.isAliMP || env.isAliMPMonitor) {
    showChannelId = '2';
  }

  try {
    const result = await getVirtualCoupons({
      token,
      businessId: type,
      pageSize,
      pageNum: 1,
      showChannelId,
      adCode,
      cityCode,
      includeOption: {
        includePromo: true,
        includeUserLimit: false,
      },
    });
    const { data = [] } = result || {};
    // 商品列表数据处理
    const exchangeGoodsList = exchangeGoodsListAdapter(data);
    return exchangeGoodsList;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getExchangeGoodsList => error', safeJsonStringify(error));
    return [];
  }
}

/**
 * @description 奖励金+钱兑换下单
 * @function couponOrderCreate
 * @returns
 */
export async function createCouponOrder(params, isDetail = false) {
  const {
    token, adCode, cityCode, longitude: lon, latitude: lat,
  } = await getTokenAndLocation();
  const api = isDetail ? detailCouponOrderCreate : couponOrderCreate;
  const {
    payAmount, // 订单整单应付金额
    itemList,
  } = params;
  let orderPort;
  if (env.isEasyBikeApp) {
    orderPort = 1; // 其实该模块只会在app中展示
  } else if (env.isAliMP || env.isAliMPMonitor) {
    orderPort = 2;
  }
  const result = await api({
    token,
    adCode,
    cityCode,
    lon,
    lat,
    payAmount,
    orderPort,
    adsource: ParamsUtil.getParams().adsource || '',
    itemList,
  });
  const { data = {}, code } = result || {};
  if (code === 0) {
    return data;
  }
}

/**
 * @description 单人返任务弹层数据适配器
 * @function taskAwardListApdater
 * @param {*} list 原始列表
 * @returns
 */
export function taskAwardListApdater(list) {
  if (list.length === 0) {
    return [];
  }

  return list.map((item) => {
    const {
      mainTitle = '',
      taskImgUrl = '',
      offerWorth,
      guideDesc = '',
      actualOfferWorth = '',
    } = item || {};

    return {
      title: mainTitle,
      logo: taskImgUrl,
      amount: offerWorth,
      guideDesc,
      actualOfferWorth,
    };
  });
}

/**
 * @description 获取单人返任务弹层数据
 * @function getTaskAwardListData
 * @param {*} options 选项参数
 * @returns
 */
export async function getTaskAwardListData(options) {
  const { isVip } = options || {};

  try {
    const { token, adCode, cityCode } = await getTokenAndLocation();
    const result = await getTaskAwardDetail({
      token,
      cityCode,
      adCode,
      ariesVip: isVip,
    });
    const { data, code } = result || {};

    if (code === 0) {
      const {
        taskCouponList = [],
        ariesVipTitle,
        guidingDesc,
        guidingButtonName,
        ariesVipUrl,
        ariesVip,
      } = data || {};

      return {
        taskAwardList: taskAwardListApdater(taskCouponList),
        ariesData: {
          ariesVip,
          ariesVipTitle,
          guidingDesc,
          guidingButtonName,
          ariesVipUrl,
        },
      };
    }

    Toast.show('网络繁忙，请稍后重试');
    return {
      taskAwardList: [],
      ariesData: {},
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getTaskAwardListData => error', safeJsonStringify(error));
    Toast.show('网络繁忙，请稍后重试');
    return {
      taskAwardList: [],
      ariesData: {},
    };
  }
}

/**
 * @description 处理兑换奖励引导规则
 * @function getRewardGuideRules
 * @param {*} rules 引导规则
 * @returns
 */
export function getRewardGuideRules(rules) {
  if (rules && rules.length > 0) {
    return rules;
  }
  // 小程序端兜底锚定到常规任务模块（接口未返回规则时）
  if (!env.isEasyBikeApp) {
    return [{
      minNumber: 0,
      maxNumber: -1,
      anchoringModuleKey: 'welfare_task',
      guidingDesc: '做任务赚奖励金',
    }];
  }
  return REWARD_GUIDE_RULES_DEFAULT;
}

/**
 * @description 视频红包数据适配器
 * @function videoRedPacketDataAdapter
 * @param {*} data 视频红包数据
 * @returns
 */
export function videoRedPacketDataAdapter(data) {
  if (!data || data.length === 0) {
    return {};
  }
  // 查找视频红包任务数据
  const info = data.find(
    (item) => item.moduleKey === FULI_MODULE.welfare_advertisement_video,
  );
  const {
    title,
    information,
    advertisementUnitId = '',
    subSceneNameEn,
    sceneNameEn,
    channelId,
    bannerAdvertisementUnitId = '',
    bannerTaskRelationCouponInfo = {},
    maxTotalAmount = 0,
    subTitle = '',
    abResult,
  } = info || {};
  const {
    stageOffers = [],
    btnName,
    taskCode = '',
    taskName,
  } = information || {};

  let hasActive = false;
  let curActiveIdx = -1;
  // 视频任务列表数据处理
  const taskList = stageOffers.map((item, index) => {
    const { totalAmount, offerStatus, stageNo } = item || {};
    // offerStatus: 3 已完成
    const finished = offerStatus === 3;

    // 判断是否有未完成的任务
    if (!hasActive && !finished) {
      hasActive = true;
      curActiveIdx = index;
    }

    return {
      amount: totalAmount,
      amountText: !finished ? `+${totalAmount}` : totalAmount,
      finished,
      stageNo,
      curActive: curActiveIdx === index,
    };
  });

  return {
    titleIcon: title || VIDEO_RED_PACKET_DEFAULT_ICON,
    hasActive,
    btnName: hasActive ? btnName : '明天来',
    taskList,
    maxTotalAmount,
    subTitle,
    baseInfo: {
      unitId: advertisementUnitId,
      taskCode,
      taskName,
      subSceneNameEn,
      sceneNameEn,
      channelId,
    },
    curActiveIdx,
    doubleAdvInformation: {
      bannerAdvertisementUnitId,
      bannerTaskRelationCouponInfo,
    },
    abResult,
  };
}

/**
 * @description 判断是否显示复访引导
 * @function judgeRevisitGuideVisible
 * @returns
 */
export async function judgeRevisitGuideVisible() {
  // 1.判断是否命中缓存
  const timestamp = Storage.getItem(STORAGE_KEY_SHOW_REVISIT_GUIDE_TIME);
  const isWithin = isWithinDays(timestamp, 90);
  if (isWithin) return false;

  try {
    // 2.判断教育弹窗配置&实验是否命中
    const { token } = await getTokenAndKey();
    const result = await getEducationPopupWindow({
      token,
      adsource: ParamsUtil.getParams().adsource || '',
    });
    const { code, data } = result || {};

    if (code === 0 && data) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * @description 推进任务完成
 * @function pushTaskFinished
 * @param {*} task 任务信息
 * @returns
 */
export async function pushTaskFinished(task, options) {
  const { taskCode = '', browseDuration } = task || {};
  const { handleParams = null } = options || {};
  let channel = 'app';
  let btnLink = '';

  if (env.isAliMP || env.isAliMPMonitor) {
    channel = 'ali';
  }
  if (task.btnLink) {
    btnLink = task.btnLink[channel];
  }

  try {
    const { token, adCode, cityCode } = await getTokenAndLocation();
    const params = {
      token,
      channelId: env.platformCode,
      cityCode,
      adCode,
      taskCode,
      btnLink,
      sceneNameEn: 'weekly_cash',
      subSceneNameEn: 'weekly_cash_app',
      browseDuration,
    };

    // 处理参数
    let newParams = {};
    if (handleParams && typeof handleParams === 'function') {
      newParams = handleParams(params);
    } else {
      newParams = { ...params };
    }

    // 推进任务完成
    const result = await finishTask(newParams);
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('jumpEvent => error', safeJsonStringify(error));
    return { success: false };
  }
}

/**
 * @description 处理任务模块信息
 * @function handleTaskModuleInfo
 * @param {*} taskModuleInfo 任务模块信息
 * @param {*} extInfo 扩展信息
 * @returns
 */
export const handleTaskModuleInfo = (taskModuleInfo, extInfo) => {
  const {
    taskType,
    mainTitle,
    taskList = [],
    isUpdate = false,
    newTaskInfo = {},
  } = extInfo || {};
  const {
    singleTaskTitle = '',
    singleTaskDesc = '',
    rewardDesc = '',
    hasAward,
    rewardAmount,
  } = taskModuleInfo || {};

  // 单一任务信息
  let singleTaskInfo = {};
  if (taskType === TASK_MODULE_TYPE.SINGLE_TASK) {
    const taskInfo = (isUpdate ? newTaskInfo : taskList[0]) || {};
    singleTaskInfo = {
      buttonName: taskInfo.btnName,
      taskStatus: taskInfo.taskStatus,
      amount: taskInfo.amount,
      disabled: taskInfo.disabled,
      taskInfo,
      taskCode: taskInfo.taskCode,
    };
  }

  return {
    ...taskModuleInfo,
    taskType,
    title: taskType === TASK_MODULE_TYPE.SINGLE_TASK ? singleTaskTitle : mainTitle,
    desc: taskType === TASK_MODULE_TYPE.SINGLE_TASK ? singleTaskDesc : rewardDesc,
    showAwardTips: taskType === TASK_MODULE_TYPE.GROUP_TASK && hasAward,
    amount: taskType === TASK_MODULE_TYPE.SINGLE_TASK ? singleTaskInfo.amount : rewardAmount,
    singleTaskInfo,
  };
};

/**
     * 新bridge任务链接参数处理，只替换宏参
     * @param {string} link - h5链接
     * @param {object} task - task任务数据
     * @returns
     */
export const replaceTaskLinkParams = async (link, task) => {
  try {
    if (!link) return '';
    const { link: res } = await mobileAdReplacementWithLink({ link });
    const { userNewId } = await getTokenAndKey();
    const resLink = res.replace('__REQUESTID__', generateUUID()).replace('__TASK_CODE__', task.taskCode).replace('__USER_NEW_ID__', userNewId);
    return resLink;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('任务链接参数处理失败', error);
    return link;
  }
};

/**
 * @description 任务列表数据适配器
 * @function taskGroupListAdapter
 * @param {*} options 原始数据
 * @returns
 */
export async function taskGroupListAdapter(options, callback) {
  const { list, isRefresh } = options || {};
  if (list.length === 0) {
    return [];
  }

  const version = await getVersion();
  // 需要自动推进完成的任务列表
  const needFinishedTasks = [];

  const newList = list.map((item) => {
    const {
      taskType,
      mainTitle,
      subTitle,
      moduleKey,
      titleIcon,
      taskCouponList = [],
      bannerAdvertisementUnitId = '',
      bannerTaskRelationCouponInfo = {},
      taskDataInfo = {},
    } = item || {};

    const newTaskCouponList = [];
    // 任务列表数据处理
    taskCouponList.forEach(async (task) => {
      const { marketPlan = '', taskStatus, btnLink } = task || {};

      // 商业化任务曝光上报
      if (marketPlan === MARKET_PLAN.MUTIL_TRACE) {
        const { app = {} } = btnLink || {};
        const { impressionTracking = '' } = app;
        const trackerUrl = await replaceTaskLinkParams(impressionTracking, task);
        const params = {
          uuid: generateUUID(),
          trackConfig: { trackerUrls: [{ trackerUrl, requestType: 'get' }] },
          jumpConfig: {},
          linkType: 'deeplink',
        };
        openAllAdsURLAndTrack(params);
      }

      if (
        marketPlan === 'remind'
        && compareVersions(version, '6.89.5') !== -1
        && taskStatus === TASK_STATUS_V2.RUNNING
      ) {
        // 消息推送开启任务推进
        needFinishedTasks.push(task);
      }

      newTaskCouponList.push({
        ...newMapTaskV2(task),
        ...task,
      });
    });

    return {
      taskType,
      mainTitle,
      subTitle,
      key: moduleKey,
      titleIcon,
      taskCouponList: newTaskCouponList,
      bannerAdvertisementUnitId,
      bannerTaskRelationCouponInfo,
      taskModuleInfo: handleTaskModuleInfo(taskDataInfo, {
        taskType,
        mainTitle,
        taskList: newTaskCouponList,
      }),
    };
  });

  // 推进任务完成
  if (needFinishedTasks.length > 0 && !isRefresh) {
    // 获取消息推送配置开关
    const { enabled = false } = await pushNotificationEnabled();
    if (enabled) {
      Promise.all(
        needFinishedTasks.map((task) => pushTaskFinished(task)),
      ).then(() => {
        callback();
      });
    }
  }

  return newList;
}

/**
 * @description 推进任务完成
 * @function handleFinishTask
 * @param {*} param 任务信息
 * @returns
 */
export async function handleFinishTask({ inspireType, bannerTaskRelationCouponInfo }) {
  const { token } = await getTokenAndKey();

  /**
   * inspireType
   * 1: 后端接口对接
   * 0: 非后端接口对接（需要前端调用任务完成接口 mars.task.jumpEvent）
   */
  // eslint-disable-next-line no-console
  console.log('handleFinishTask => inspireType', inspireType);
  if (inspireType) {
    return { success: true };
  }
  const {
    taskCode,
    subSceneNameEn,
    sceneNameEn,
  } = bannerTaskRelationCouponInfo || {};

  try {
    const result = await finishTask({
      token,
      channelId: env.platformCode,
      taskCode,
      sceneNameEn,
      subSceneNameEn,
    });
    const { success, msg } = result || {};
    if (!success) {
      Toast.show(msg || '任务未完成');
      return { success: false, msg };
    }
    return { success: true };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('handleFinishTask => error', safeJsonStringify(error));
    Toast.show((error && error.msg) || '任务未完成');
    return { success: false, error };
  }
}

/**
 * @description 加载并展示奖励结果弹窗
 * @function loadRewardResultPlatformAds
 * @param {*} options 签到信息
 * @returns
 */
export async function loadRewardResultPlatformAds({
  inspireType,
  bannerTaskRelationCouponInfo = {},
  timeout = '2',
  scene = 'rebateBonus',
  ...rest
} = {}) {
  try {
    const { success } = await handleFinishTask({ inspireType, bannerTaskRelationCouponInfo });
    if (!success) {
      return {};
    }
    const params = {
      pageName: 'rewardResult',
      timeout,
      scene,
      adsource: ParamsUtil.getParams().adsource || '',
      ...rest,
    };
    // eslint-disable-next-line no-console
    console.log('loadRewardResultPlatformAds => params', safeJsonStringify(params));
    const loadRes = await loadPlatformAds(params);

    if (loadRes && loadRes.pageId) {
      eventBus.$emit('playCollectAnimate');
      // 展示弹窗
      const res = await showPlatformAds({
        pageId: loadRes.pageId,
      });
      return res;
    }

    // 当广告加载失败时返回默认值
    return {};
  } catch (error) {
    // 发生错误时也返回默认值
    return {};
  }
}

/**
 * @description 加载并展示签到反馈弹窗
 * @function loadSignResultPopup
 */
async function loadSignResultPopup({
  otherInfo = {},
  inspireType,
  bannerTaskRelationCouponInfo,
  adsInfo = {},
  onAdClear,
  onAllPopupClosed,
} = {}) {
  try {
    const loadRes = await loadPlatformAds({
      pageName: 'signResult',
      timeout: '2',
      scene: 'rebateBonus',
      adsource: ParamsUtil.getParams().adsource || '',
      otherInfo,
      adsInfo,
    });
    if (!loadRes || !loadRes.pageId) return {};

    eventBus.$emit('playCollectAnimate');
    const showRes = await showPlatformAds({
      pageId: loadRes.pageId,
    });
    const {
      action,
      adsInfo: adInfoRes,
      inspireType: newInspireType,
    } = showRes || {};
    const { rewardTotalCount = '' } = adInfoRes || {};

    // 看广告成功 → 清除广告信息，完成任务，再展示新的反馈弹窗
    if (action === 'bannerViewSuccess') {
      if (onAdClear) onAdClear();
      await handleFinishTask({
        inspireType: newInspireType || inspireType,
        bannerTaskRelationCouponInfo,
      });
      return loadSignResultPopup({
        otherInfo: {
          ...otherInfo,
          data: {
            rewardCount: rewardTotalCount || '',
            isTotalReward: true,
          },
        },
        inspireType: newInspireType || inspireType,
        bannerTaskRelationCouponInfo,
        onAdClear,
        onAllPopupClosed,
      });
    }
    // 用户主动关闭弹窗 → 触发任务弹窗回调
    if (action === 'close' && onAllPopupClosed) onAllPopupClosed();
    return showRes;
  } catch (error) {
    return {};
  }
}

/**
 * @description 加载并展示签到弹窗
 * @function loadAndShowSignPopup
 * @param {*} options 签到信息
 * @returns
 */
export async function loadAndShowSignPopup(options = {}) {
  const {
    signInExtInfo = {},
    signInAdInfo = {},
    onSign,
    onAdClear,
    onAllPopupClosed,
    ubtData = {},
  } = options;

  // 兼容老调用方：优先从 signInExtInfo 取，降级从 options 顶层取
  const isVip = signInExtInfo.isVip ?? options.isVip;
  const signInPopupList = signInExtInfo.signInPopupList
    || options.signInPopupList || [];
  const isRepeatSigned = signInExtInfo.isRepeatSigned
    ?? options.isRepeatSigned ?? false;
  const signInPopupTitle = signInExtInfo.signInPopupTitle
    || options.signInPopupTitle || '';
  const signInPopupDesc = signInExtInfo.signInPopupDesc
    || options.signInPopupDesc || '';
  const isPureVersion = options.isPureVersion ?? false;
  const isFirstSign = options.isFirstSign ?? false;

  const bannerAdvertisementUnitId = signInAdInfo?.bannerAdvertisementUnitId
    || options.bannerAdvertisementUnitId || '';
  const bannerTaskRelationCouponInfo = signInAdInfo?.bannerTaskRelationCouponInfo
    || options.bannerTaskRelationCouponInfo || null;
  try {
    // 加载签到弹窗埋点
    SPM.customEventWithPointId({
      pointId: 'triggerExpose_loadSignPopup',
      businessInfo: {
        params_info: safeJsonStringify(options),
      },
    });
  } catch (error) {
    // 埋点上报失败，不阻断后续逻辑
  }

  const { resourceId } = signInExtInfo.extraConfig || {};
  const adsInfo = processAdParams({
    bannerAdvertisementUnitId,
    bannerTaskRelationCouponInfo,
    extraAdUnitId: resourceId,
  });
  const repeatSignedInfo = {};

  // 已在其它端签到
  if (isRepeatSigned) {
    repeatSignedInfo.isThirdPartySigned = true;
    repeatSignedInfo.thirdPartySignedTitle = signInPopupTitle;
    repeatSignedInfo.thirdPartySignedDesc = signInPopupDesc;
  }

  const otherInfo = {
    signInData: signInPopupList,
    isVip,
    data: {
      isTotalReward: true,
    },
    signInExtInfo: {
      bonusList: signInPopupList,
      ...signInExtInfo,
    },
    ubtData,
    ...repeatSignedInfo,
  };

  // 加载弹窗
  const loadRes = await loadPlatformAds({
    pageName: 'signIn2',
    timeout: '2',
    // 是否展示广告（本期不做）
    scene: 'rebateBonus',
    adsInfo: !isPureVersion ? adsInfo : {},
    otherInfo,
  });

  if (loadRes && loadRes.pageId) {
    // 签到弹窗曝光埋点
    SPM.moduleExpose({
      moduleId: 'Welfare_newsignpop',
      contentId: 'Welfare_newsignpop',
      businessInfo: {
        adv: adsInfo.adUnitId && adsInfo.rewardCount ? '0' : '1',
      },
    });

    // 展示弹窗
    const showRes = await showPlatformAds({
      pageId: loadRes.pageId,
    });
    const { action, adsInfo: adInfoRes, inspireType } = showRes || {};
    const { rewardTotalCount = '', extraAdUnitId } = adInfoRes || {};
    const { bountyCountToday = 0 } = signInExtInfo || {};
    let isNeedToSign = false; // 是否需要签到
    let isNeedFinishTask = false; // 是否广告成功去完成任务
    let isLoadRewardResultPlatformAds = false; // 是否加载反馈弹窗
    let _adsInfo = {};
    // const adUnitId = '28df39b08dcc40b4a32bceb2e1a81d7b';
    // 根据签到状态和广告结果计算反馈弹窗展示金额
    let rewardCount = 0;
    switch (action) {
      // action有签到signInV2、
      // 签到并看广告成功signBannerSuccess、
      // 签到并看广告失败signBannerFailure、
      // 单独看广告成功bannerViewSuccess、关闭
      case 'signInV2':
        isNeedToSign = true;
        isLoadRewardResultPlatformAds = true;
        rewardCount = bountyCountToday;
        _adsInfo = { ...adInfoRes, adUnitId: extraAdUnitId }; // 签到后，此时在反馈弹窗展示虚拟的广告
        break;
      case 'signBannerSuccess':
        isNeedToSign = true;
        isNeedFinishTask = true;
        isLoadRewardResultPlatformAds = true;
        rewardCount = +bountyCountToday + (+rewardTotalCount || 0);
        if (onAdClear) onAdClear();
        break;
      case 'signBannerFailure':
        isNeedToSign = true;
        isLoadRewardResultPlatformAds = true;
        rewardCount = bountyCountToday;
        _adsInfo = { ...adInfoRes, adUnitId: extraAdUnitId };
        break;
      case 'signAdSuccess':
        isNeedFinishTask = true;
        isLoadRewardResultPlatformAds = true;
        rewardCount = rewardTotalCount;
        if (onAdClear) onAdClear();
        break;
      case 'close':
        if (onAllPopupClosed) onAllPopupClosed();
        break;
      default:
        break;
    }
    // 根据标志位依次处理：签到 → 任务完成 → 反馈弹窗
    // 先签到刷新奖励金，再打开反馈弹窗展示最新数据
    if (isNeedToSign && onSign) {
      await onSign();
    }

    if (isNeedFinishTask) {
      await handleFinishTask({
        inspireType,
        bannerTaskRelationCouponInfo,
      });
    }
    if (isLoadRewardResultPlatformAds) {
      // 所有数据原封不动传递给结果弹窗
      loadSignResultPopup({
        otherInfo: {
          ...otherInfo,
          data: {
            rewardCount: rewardCount || '',
            isTotalReward: true,
            fromAction: action,
          },
        },
        inspireType,
        bannerTaskRelationCouponInfo,
        adsInfo: _adsInfo,
        onAdClear,
        onAllPopupClosed,
      });
    }
    return { ...showRes, isNeedToSign };
  }

  // 弹窗展示失败埋点统计
  SPM.customEventWithPointId({
    pointId: 'triggerExpose_popnone',
    businessInfo: {
      errorInfo: safeJsonStringify(loadRes),
      isFirstSign: isFirstSign ? '1' : '0',
    },
  });

  return {};
}

/**
 * @description 获取电商返佣banner
 * @function getRakeBackBanner
 * @returns
 */
export async function getRakeBackBanner() {
  try {
    const { token } = await getTokenAndKey();
    const result = await queryDeliverBanner({
      token,
      newVersion: true,
      scene: 'rebateBonus',
    });

    if (result && result.data) {
      return { success: true, result };
    }
    return { success: false, result: null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getRakeBackBanner => error', safeJsonStringify(error));
    return { success: false, result: null };
  }
}

/**
 * @description 电商返佣banner信息适配器
 * @function rakeBackBannerAdapter
 * @param {*} info 电商返佣banner信息
 * @returns {Object} 电商返佣banner信息
 */
export function rakeBackBannerAdapter(info) {
  const { data } = info || {};
  const {
    isShow,
    bannerActivityTaskVO = null,
    welfareBannerDataVO,
    moduleTitle,
    moduleTitleStyle,
    returnProp,
  } = data || {};
  const { bannerDataList = [], title = '' } = welfareBannerDataVO || {};
  return {
    bannerList: bannerDataList,
    isShow: !!isShow,
    isShowBannerList: bannerDataList.length === 2,
    bannerActivityTask: bannerActivityTaskVO,
    text: title,
    title: moduleTitle || '点外卖完单返奖励金',
    rebateRatio: returnProp,
    isWeekend: moduleTitleStyle === 'weekStyle',
  };
}

async function reportTaskClick(h5Link, taskInfo, errorMessage = '') {
  const { taskCode = '' } = taskInfo || {};
  const { userNewId } = await getTokenAndKey();
  SPM.clickButton({
    buttonName: 'platform_task_click',
    businessInfo: {
      task_id: taskCode,
      userNewId,
      link: h5Link,
      errorMessage,
    },
  });
}

/**
 * 任务h5检测链接上报
 * @param {string} h5Link
 */
export const requestTaskMonitoring = async (h5Link, taskInfo) => {
  try {
    const res = await Axios.get(h5Link);
    if (res.data.code === 0 || res.data.code === '0') {
      reportTaskClick(h5Link, taskInfo, `上报成功-${safeJsonStringify(res.data.data)}`);
    } else {
      reportTaskClick(h5Link, taskInfo, `上报失败-${safeJsonStringify(res.data)}`);
    }
  } catch (error) {
    reportTaskClick(h5Link, taskInfo, `error-上报失败-${error.message}`);
  }
};


/**
 * @description 任务链接参数处理
 * @function taskLinkParamsHandle
 * @param {string} link - h5链接
 * @param {object} task - task任务数据
 * @returns {Promise<string>} 处理后的链接
 */
export async function taskLinkParamsHandle(link, task) {
  try {
    const res = await mobileAdReplacementWithLink({ link });
    const { userNewId } = await getTokenAndKey();
    const resLink = ParamsUtil.addParams(res.link, {
      userid: userNewId,
      taskCode: task.taskCode,
    }).replace('__REQUESTID__', generateUUID()).replace('__TASK_CODE__', task.taskCode).replace('__USER_NEW_ID__', userNewId);
    return resLink;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('任务链接参数处理失败', error);
    return link;
  }
}

/**
 * @description 玩法引导弹窗信息适配器
 * @function playGuidePopupInfoAdapter
 * @param {*} data 玩法引导弹窗信息
 * @returns 玩法引导弹窗信息
 */
export const playGuidePopupInfoAdapter = (data = {}) => {
  // 解构并设置默认值
  const {
    logo = '',
    skuId = '',
    goodsImageUrl = PLAY_GUIDE_DEFAULT_IMGS.goodsImageUrl,
    twoFramesUrl = PLAY_GUIDE_DEFAULT_IMGS.twoFramesUrl,
    threeFramesUrl = PLAY_GUIDE_DEFAULT_IMGS.threeFramesUrl,
    floatingLayerUrls = [],
  } = data || {};

  // 处理浮动层URL
  const processedFloatingLayerUrl = floatingLayerUrls.length > 0
    ? floatingLayerUrls.map((url, index) => ({
      id: index + 1,
      imgUrl: url,
    }))
    : WELFARE_PLAY_GUIDE_LIST;

  return {
    logo,
    skuId,
    goodsImageUrl,
    twoFramesUrl,
    threeFramesUrl,
    floatingLayerUrls: processedFloatingLayerUrl,
  };
};

/**
 * @description 页面事件上传
 * @function uploadEvent
 */
export const uploadEvent = async () => {
  try {
    const { token, adCode, cityCode } = await getTokenAndLocation();
    const cityInfo = { adCode, cityCode };

    await eventUpload({
      eventType: '8000',
      token,
      buryType: 1,
      action: 'user.platform.event.upload',
      adSource: ParamsUtil.getParam('adsource') || '',
      ...cityInfo,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('uploadEvent => error', safeJsonStringify(error));
  }
};

/**
 * @description 获取页面模块配置列表
 * @function getPageModuleList
 * @param {*} params 参数
 * @param {string} params.token - 用户token
 * @param {number} params.pageType - 页面类型
 * @returns {Promise<{success: boolean, moduleList: Array}>} 页面模块配置列表
 */
export const getPageModuleList = async (params) => {
  try {
    const entranceSource = ParamsUtil.getParam('entranceSource');
    const result = await getPageModule({
      ...params,
      pageTab: ParamsUtil.getParam('pageTab') || '',
      ...(entranceSource && { entranceSource }),
    });
    const { data } = result || {};
    const { moduleList = [] } = data || {};
    return { success: true, moduleList };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getPageModuleList => error', safeJsonStringify(error));
    return { success: false, moduleList: [] };
  }
};


/**
 * @description 根据userNewId查询奖励金配置信息
 * @function getAutoDeductStatusInfo
 * @returns {Promise<{success: boolean, msg?: string, isOpen?: boolean}>}
 */
export const getAutoDeductStatusInfo = async () => {
  try {
    const { token } = await getTokenAndKey();
    const result = await getAutoDeductStatus({ token });
    const { code, msg, data } = result || {};

    if (code === 0) {
      return {
        isOpen: data.isOpen,
        success: true,
        msg,
      };
    }

    return {
      success: false,
      msg: msg || '获取状态失败',
    };
  } catch (error) {
    return {
      success: false,
      msg: '网络异常，请稍后重试',
    };
  }
};

/**
 * @description 更新奖励金自动抵扣开关
 * @function updateAutoDeductStatusInfo
 * @param {boolean} isOpen - 是否开启自动抵扣
 * @returns {Promise<{success: boolean, msg?: string, isSuccess?: boolean}>}
 */
export const updateAutoDeductStatusInfo = async (isOpen) => {
  try {
    const { token } = await getTokenAndKey();
    const result = await updateAutoDeductStatus({ token, isOpen });
    const { code, msg, data } = result || {};

    if (code === 0) {
      return {
        isSuccess: data,
        success: true,
        msg,
      };
    }

    return {
      success: false,
      msg: msg || '操作失败，请稍后再试',
    };
  } catch (error) {
    return {
      success: false,
      msg: '网络异常，请稍后重试',
    };
  }
};

/**
 * @description 锚点定位到指定模块并添加高亮动画
 * @function anchorToModule
 * @param {Object} guideInfo - 引导信息
 * @param {string} guideInfo.anchoringModuleKey - 锚点模块的DOM id
 * @param {number|null} anchorTimer - 当前定时器引用
 * @returns {number|null} 新的定时器引用
 */
export function anchorToModule(guideInfo, anchorTimer) {
  const { anchoringModuleKey = '' } = guideInfo || {};
  const moduleEl = document.getElementById(anchoringModuleKey);
  if (!moduleEl) return anchorTimer;

  // 移除上一次还没有被销毁的定时器
  if (anchorTimer) {
    clearTimeout(anchorTimer);
  }

  const { offsetTop } = moduleEl;
  // 添加动画样式
  moduleEl.classList.add('shine-light-animation');
  // 动态获取页面头部高度，兼容单tab(v3-95的高度)和双tab(v4/v5-135的高度)
  const headerEl = document.querySelector('.header-tab-area') ? 135 : 95;
  window.scrollTo({
    top: offsetTop - headerEl,
    behavior: 'smooth',
  });

  // 移除动画样式
  const timer = setTimeout(() => {
    moduleEl.classList.remove('shine-light-animation');
  }, 3000);

  return timer;
}
/**
 * @description 获取页面配置
 * @function getPageConfig
 * @param {string} key - 页面key
 * @param {boolean} isMixPage - 是否是聚合页面
 * @param {boolean} isHitchCarFare - 是否是顺风车心智版本（覆盖福利中心两个 tab 的头部）
 * @returns {Object} 页面配置
 */
export const getPageConfig = (key, isMixPage = false, isHitchCarFare = false) => {
  const config = WELFARE_CENTER_PAGE_CONFIG[key];
  const commonInfo = isMixPage ? WELFARE_CENTER_COMMON_HEADER_INFO : {};
  // 顺风车心智版本：覆盖福利中心两个 tab 的头部
  const hitchInfo = isHitchCarFare ? WELFARE_CENTER_HITCH_HEADER_INFO : {};

  if (config) {
    return { ...config, ...commonInfo, ...hitchInfo };
  }
  return {
    ...WELFARE_CENTER_PAGE_CONFIG.bounty,
    ...commonInfo,
    ...hitchInfo,
  };
};

/**
 * @description 获取页面样式版本
 * @function handleWelfareStyleVersion
 * @returns {Promise<{success: boolean, data: Object}>} 页面样式版本
 */
export const handleWelfareStyleVersion = async () => {
  try {
    const { token } = await getTokenAndKey();
    const result = await getWelfareStyleVersion({
      token,
      pageTab: ParamsUtil.getParam('pageTab') || '',
    });
    const { data } = result || {};
    return { success: true, data };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getWelfareStyleVersion => error', safeJsonStringify(error));
    return { success: false, data: null };
  }
};

/**
 * @description 判断是否为福利中心顺风车心智版本
 * @function handleHitchCarFareCheck
 * @returns {Promise<{success: boolean, isHitchCarFare: boolean}>}
 */
export const handleHitchCarFareCheck = async () => {
  try {
    const { token } = await getTokenAndKey();
    const result = await checkIsWelfareHitchCarFare({
      token,
      pageTab: ParamsUtil.getParam('pageTab') || '',
    });
    const { data } = result || {};
    return { success: true, isHitchCarFare: !!(data && data.isHitchCarFare) };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('checkIsWelfareHitchCarFare => error', safeJsonStringify(error));
    return { success: false, isHitchCarFare: false };
  }
};

/**
 * 查询用户桌面小组件状态
 * 统一封装品牌获取 + 接口调用
 * @returns {Promise<{canAddComponent: boolean, hasAddedComponent: boolean}>}
 * - canAddComponent: 是否可添加小组件（未添加过）
 * - hasAddedComponent: 是否已添加小组件
 */
export const queryUserWidgetStatus = async () => {
  try {
    const brand = await getManufacturer();
    if (!brand) {
      return { canAddComponent: false, hasAddedComponent: false };
    }
    const [{ token }, version] = await Promise.all([
      getTokenAndKey(),
      getVersion(),
    ]);
    const { code, data } = await getUserComponent({
      version,
      token,
      manufacturer: brand,
    }) || {};
    if (code !== 0 || !data) {
      return { canAddComponent: false, hasAddedComponent: false };
    }
    return {
      canAddComponent: !!data.canAddComponent,
      hasAddedComponent: !!data.hasAddedComponent,
    };
  } catch (error) {
    return { canAddComponent: false, hasAddedComponent: false };
  }
};
// 判断当前任务是否为返佣任务
export function isFackBackTask(marketPlan) {
  return marketPlan === 'rebate_commodity';
}
